"use client";

import { useState, useEffect, useRef } from "react";
import { ItemCategory } from "@/lib/types";
import {
  getBrandSuggestions,
  searchBrands,
} from "@/lib/listing/brandSuggestions";
import { isCatalogBrand } from "@/lib/listing/listingIdentityPolicy";
import { ChevronDownIcon, XMarkIcon } from "@heroicons/react/24/outline";

interface BrandSelectorProps {
  value: string;
  onChange: (brand: string) => void;
  category: ItemCategory | string | null;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  required?: boolean;
}

/**
 * Catalog-only brand picker: search filters the list; selecting commits.
 * Typing never writes free-text into the listing. Clear / skip leaves brand empty.
 */
export default function BrandSelector({
  value,
  onChange,
  category,
  placeholder = "Search and choose a brand",
  className = "",
  disabled = false,
  required = false,
}: BrandSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const valueIsCatalog = isCatalogBrand(value, category);
  const hasLegacyValue = Boolean(value.trim()) && !valueIsCatalog;

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (searchTerm.trim()) {
        setSuggestions(searchBrands(searchTerm, category));
      } else {
        setSuggestions(getBrandSuggestions(category).slice(0, 12));
      }
    }, 0);
    return () => window.clearTimeout(timer);
  }, [searchTerm, category]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (brand: string) => {
    onChange(brand);
    setIsOpen(false);
    setSearchTerm("");
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange("");
    setSearchTerm("");
    setIsOpen(false);
  };

  const handleSkip = () => {
    onChange("");
    setSearchTerm("");
    setIsOpen(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    if (!isOpen) setIsOpen(true);
  };

  const handleInputFocus = () => {
    if (disabled) return;
    setIsOpen(true);
    setSearchTerm("");
  };

  const inputDisplayValue = isOpen ? searchTerm : value;

  return (
    <div className={`relative w-full ${className}`} ref={dropdownRef}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={inputDisplayValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          disabled={disabled}
          required={required && !value.trim()}
          placeholder={placeholder}
          className={`w-full px-3 py-2 pr-20 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
            hasLegacyValue ? "border-amber-400" : "border-border"
          } ${
            disabled
              ? "bg-secondary cursor-not-allowed opacity-60"
              : "bg-surface"
          }`}
          autoComplete="off"
          role="combobox"
          aria-expanded={isOpen}
          aria-autocomplete="list"
          aria-controls="brand-selector-listbox"
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-2">
          {value && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="p-1 text-text-light hover:text-text-light rounded-full hover:bg-tertiary"
              aria-label="Clear brand"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          )}
          <button
            type="button"
            onClick={() => !disabled && setIsOpen(!isOpen)}
            className="p-1 text-text-light hover:text-text-light rounded-full hover:bg-tertiary"
            disabled={disabled}
            aria-label="Toggle brand list"
          >
            <ChevronDownIcon
              className={`h-4 w-4 transition-transform ${
                isOpen ? "rotate-180" : ""
              }`}
            />
          </button>
        </div>
      </div>

      {hasLegacyValue && !disabled && (
        <p className="mt-1 text-xs text-amber-700">
          This brand isn&apos;t on our list. Clear it or choose one below.
        </p>
      )}

      {isOpen && !disabled && (
        <div
          id="brand-selector-listbox"
          role="listbox"
          className="absolute z-50 w-full mt-1 bg-surface border border-border rounded-lg shadow-lg max-h-60 overflow-auto"
        >
          {suggestions.length > 0 ? (
            <ul className="py-1">
              {suggestions.map((brand) => (
                <li key={brand} role="option" aria-selected={value === brand}>
                  <button
                    type="button"
                    onClick={() => handleSelect(brand)}
                    className="w-full text-left px-4 py-2 hover:bg-tertiary focus:bg-secondary focus:outline-none"
                  >
                    {brand}
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <div className="px-4 py-3 text-sm text-text-light">
              No match. Skip brand if you don&apos;t see yours.
            </div>
          )}
          <div className="border-t border-border px-3 py-2">
            <button
              type="button"
              onClick={handleSkip}
              className="text-sm text-primary hover:underline"
            >
              Skip brand
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
